import axios, { AxiosInstance } from 'axios';
import config from '../config';
import authService from './authService';

// APIのベースURLを設定
const baseURL = `${config.api.baseURL}/api`;
console.log('API Client - ベースURL設定:', baseURL);

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // CORSリクエストでクッキーを送信
  timeout: 30000, // 30秒タイムアウト
});

// デバッグモードの設定
const DEBUG = config.DEBUG || false;

// リクエスト重複防止とレート制限
const pendingRequests = new Map<string, Promise<any>>();
const lastRequestTimes = new Map<string, number>();
const MIN_REQUEST_INTERVAL = 1000; // 最小リクエスト間隔（1秒）

// リクエストインターセプター
api.interceptors.request.use(
  async (config) => {
    // ログイン処理中かどうかを確認
    const isLoginInProgress = sessionStorage.getItem('login_in_progress') === 'true';
    
    // リクエストキーの生成（URL + メソッド）
    const requestKey = `${config.method?.toUpperCase()}_${config.url}`;
    const now = Date.now();
    
    // 重複リクエストの防止
    if (pendingRequests.has(requestKey)) {
      console.log(`リクエスト重複防止: ${requestKey}`, {
        url: config.url,
        method: config.method,
        timestamp: new Date().toISOString()
      });
      // 既存のリクエストを待つ
      try {
        return await pendingRequests.get(requestKey);
      } catch (error) {
        // エラーでも続行（新しいリクエストを作成）
        pendingRequests.delete(requestKey);
      }
    }
    
    // レート制限チェック（システム関連エンドポイントのみ）
    if (config.url && config.url.includes('/system')) {
      const lastRequestTime = lastRequestTimes.get(requestKey) || 0;
      const timeSinceLastRequest = now - lastRequestTime;
      
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
        console.log(`レート制限: ${requestKey} - ${waitTime}ms 待機`, {
          url: config.url,
          lastRequest: new Date(lastRequestTime).toISOString(),
          waitTime
        });
        
        // 待機処理
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      // 最後のリクエスト時刻を更新
      lastRequestTimes.set(requestKey, Date.now());
    }
    
    // URLの正規化（クエリパラメータの最適化）
    if (config.url && config.url.includes('?')) {
      try {
        // クエリパラメータを含むURLを解析
        const [basePath, queryString] = config.url.split('?');
        const params = new URLSearchParams(queryString);
        const optimizedParams = new URLSearchParams();
        
        // URLSearchParamsの互換性対応
        params.forEach((value, key) => {
          if (value && value.trim() !== '') {
            // 日付パラメータは特別処理（エンコードしない）
            if (key.includes('date') && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
              optimizedParams.append(key, value);
            } else {
              // その他のパラメータは適切にエンコード
              try {
                optimizedParams.append(key, decodeURIComponent(value));
              } catch (e) {
                // デコードに失敗した場合はそのまま使用
                optimizedParams.append(key, value);
              }
            }
          }
        });
        
        // 最適化されたURLを再構築
        const optimizedQuery = optimizedParams.toString();
        config.url = basePath + (optimizedQuery ? '?' + optimizedQuery : '');
        
        console.log('URL最適化完了:', {
          original: basePath + '?' + queryString,
          optimized: config.url
        });
      } catch (e) {
        console.warn('URL最適化エラー:', e, 'URL:', config.url);
      }
    }
    
    // ログイン処理中はトークンチェックをスキップ
    if (isLoginInProgress && config.url && config.url.includes('/auth/login')) {
      console.log('ログイン処理中のリクエスト - トークンチェックをスキップ');
      return config;
    }
    
    // 毎回トークンを取得して最新の状態を保つ
    const token = authService.getToken();
    
    // トークン設定のデバッグ情報
    const isImportantEndpoint = config.url && (
      config.url.includes('/auth/') || 
      config.url.includes('/system') ||  // '/system'で始まるすべてのパスを重要エンドポイントとして扱う
      config.url.includes('/orders') ||
      config.url.includes('/validate-token')
    );
    
    if (token) {
      // 標準的なBearerトークンを設定
      config.headers['Authorization'] = `Bearer ${token}`;
      
      // PHP-FPM用の分割トークン設定（長いトークンの場合）を削除
      /*
      if (token.length > 200) {
        const midPoint = Math.floor(token.length / 2);
        const part1 = token.substring(0, midPoint);
        const part2 = token.substring(midPoint);
        
        config.headers['X-Auth-Token-Part1'] = part1;
        config.headers['X-Auth-Token-Part2'] = part2;
        
        console.log('長いトークンを分割して送信:', {
          totalLength: token.length,
          part1Length: part1.length,
          part2Length: part2.length
        });
      }
      */
      
      // 追加のヘッダー設定（PHP-FPM互換性のため）
      config.headers['Accept'] = 'application/json';
      config.headers['Content-Type'] = config.headers['Content-Type'] || 'application/json';
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
      
      // グローバルヘッダーも更新（念のため）
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // ログ出力は特定のエンドポイントのみに制限
      if (isImportantEndpoint) {
        console.log('APIクライアント - Bearer認証トークンを設定 (重要エンドポイント)', {
          url: config.url,
          tokenLength: token.length,
          headers: !!config.headers['Authorization'],
          hasSplitToken: !!(config.headers['X-Auth-Token-Part1'] && config.headers['X-Auth-Token-Part2']),
          timestamp: new Date().toISOString()
        });
      }
    } else {
      // 認証関連のエンドポイントでのみ警告を表示（ログイン時などは警告を出さない）
      if (isImportantEndpoint && !isLoginInProgress) {
        console.warn('APIクライアント - 認証トークンが見つかりません', {
          url: config.url,
          method: config.method,
          timestamp: new Date().toISOString()
        });
      }
      
      // 認証ヘッダーが設定されていた場合は削除
      if (config.headers['Authorization']) {
        delete config.headers['Authorization'];
      }
      if (config.headers['X-Auth-Token-Part1']) {
        delete config.headers['X-Auth-Token-Part1'];
      }
      if (config.headers['X-Auth-Token-Part2']) {
        delete config.headers['X-Auth-Token-Part2'];
      }
    }
    
    // リクエスト情報をログに出力（機密情報は除外）
    const logInfo = {
      method: config.method?.toUpperCase(),
      url: config.url,
      hasToken: !!token,
      hasAuthHeader: !!config.headers['Authorization'],
      hasSplitToken: !!(config.headers['X-Auth-Token-Part1'] && config.headers['X-Auth-Token-Part2'])
    };
    
    // 特定のエンドポイントに関するログは詳細に出力
    if (isImportantEndpoint) {
      console.log('API重要リクエスト:', logInfo);
    } else {
      // 通常のリクエストはデバッグモードでのみ詳細ログ
      if (DEBUG) {
        console.log('APIリクエスト:', logInfo);
      }
    }
    
    return config;
  },
  (error) => {
    console.error('APIリクエスト前処理エラー:', error);
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
api.interceptors.response.use(
  (response) => {
    // pendingRequestsの管理
    const requestKey = `${response.config.method?.toUpperCase()}_${response.config.url}`;
    pendingRequests.delete(requestKey);
    
    // 特定のエンドポイントに関するレスポンスのみログ出力
    if (response.config.url && (
      response.config.url.includes('/system') ||
      response.config.url.includes('/auth/validate-token')
    )) {
      console.log(`APIレスポンス [${response.config.url}]:`, {
        status: response.status,
        data: response.data
      });
    }
    return response;
  },
  async (error) => {
    // pendingRequestsの管理
    if (error.config) {
      const requestKey = `${error.config.method?.toUpperCase()}_${error.config.url}`;
      pendingRequests.delete(requestKey);
    }
    
    // エラーレスポンスをログに出力
    console.error('APIレスポンスエラー:', {
      status: error.response?.status,
      url: error.config?.url,
      message: error.message,
      timestamp: new Date().toISOString()
    });
    
    // ログイン処理中かどうかを確認
    const isLoginInProgress = sessionStorage.getItem('login_in_progress') === 'true';
    
    // 認証エラーの場合
    if (error.response?.status === 401) {
      const reqUrl: string = error.config?.url || '';
      console.warn('401認証エラーを検出:', { url: reqUrl, timestamp: new Date().toISOString() });

      // ログイン処理中、またはログイン/トークン検証系のリクエストの401は
      // ここでは処理しない（各画面側で処理する／ログイン失敗はログイン画面で扱う）。
      const isAuthEndpoint =
        reqUrl.includes('/auth/login') ||
        reqUrl.includes('/auth/validate-token') ||
        reqUrl.includes('/auth/refresh');

      if (isLoginInProgress || isAuthEndpoint) {
        return Promise.reject(error);
      }

      // 認証切れ: 認証情報をクリアしてログイン画面へリダイレクトする。
      // 既にログイン画面にいる、またはリダイレクト処理中の場合は二重実行しない。
      const currentPath = window.location.pathname;
      const alreadyRedirecting = sessionStorage.getItem('auth_redirect_attempted') === 'true';

      if (!currentPath.includes('/login') && !alreadyRedirecting) {
        console.log('認証が切れたためログイン画面にリダイレクトします');
        sessionStorage.setItem('auth_redirect_attempted', 'true');
        try {
          authService.clearAuth();
        } catch (e) {
          // クリアに失敗してもリダイレクトは行う
        }
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// グローバルなaxiosインスタンスにもデフォルトヘッダーを設定
const setupGlobalHeaders = () => {
  const token = authService.getToken();
  if (token) {
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }
};

// 初期設定を実行
setupGlobalHeaders();

// 拡張されたAPIインターフェース
interface ExtendedAxiosInstance extends AxiosInstance {
  setupHeaders: () => void;
}

// 外部からヘッダー再設定を可能にする
(api as ExtendedAxiosInstance).setupHeaders = setupGlobalHeaders;

export default api as ExtendedAxiosInstance; 